<template>
    <div class="armour-display mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h4 class="mb-0">Armour</h4>
            <small>{{ occupiedSlots }}/{{ MAX_ARMOUR_SLOTS }} Armour Slots used</small>
            <button
                type="button"
                class="btn btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#armourModal"
            >
                <font-awesome-icon icon="plus" class="me-1" />Add
            </button>
        </div>

        <hr />

<div class="armour-board">
            <div
                v-for="item in character.armour || []"
                :key="item.name"
                class="armour-item-card"
            >
                <div class="armour-item-header">
                    <strong
                        class="armour-item-name"
                        @click="selectedArmour = item.name"
                        data-bs-toggle="modal"
                        data-bs-target="#viewArmourModal"
                    >{{ item.name || "Unnamed Armour" }}</strong>
                    <span class="armour-item-dice">{{ armourDice(item) }} Skill Dice</span>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-light armour-roll-button"
                        :disabled="armourDice(item) === 0"
                        title="Roll armour Skill Dice"
                        @click.stop="rollArmourDice(item)"
                    >
                        <font-awesome-icon icon="dice" />
                    </button>
                </div>

                <div class="armour-slot-list">
                    <div
                        v-for="(slot, slotIndex) in slotStates(item)"
                        :key="slotIndex"
                        class="armour-slot-row"
                    >
                        <div class="armour-slot-icon" aria-hidden="true">
                            <font-awesome-icon icon="shield-halved" />
                        </div>
                        <div class="armour-slot-content">
                            <div class="armour-slot-title">SLOT {{ slotIndex + 1 }}</div>
                            <div class="armour-slot-health">
                                <div class="armour-hp-track">
                                    <button
                                        v-for="hpPoint in ARMOUR_SLOT_HP"
                                        :key="hpPoint"
                                        type="button"
                                        class="armour-hp-point"
                                        :class="{ filled: hpPoint <= slot.hp }"
                                        :aria-label="`Set Slot ${slotIndex + 1} to ${hpPoint === slot.hp ? hpPoint - 1 : hpPoint}/2 HP`"
                                        @click="toggleSlotDamage(item, slotIndex, hpPoint)"
                                    ></button>
                                </div>
                                <strong v-if="slot.hp === 0" class="text-danger">BROKEN</strong>
                            </div>
                            <div class="armour-special-title">SPECIAL</div>
<div class="armour-special-body">
                                <div
                                    v-if="armourAbilities(item).length"
                                    :class="{ 'armour-special-struck': !armourAbilitiesActive(item) }"
                                >
                                    <span
                                        v-for="(ability, abilityIndex) in armourAbilities(item)"
                                        :key="abilityIndex"
                                    >
                                        <strong>{{ ability.name }}</strong><span v-if="ability.description">: {{ ability.description }}</span><span v-if="abilityIndex < armourAbilities(item).length - 1"> </span>
                                    </span>
                                </div>
                                <span v-else>—</span>
                                <small v-if="!armourAbilitiesActive(item)" class="armour-special-inactive">Fully broken</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-for="n in emptySlots" :key="`empty-${n}`" class="armour-item-card armour-empty-card">
                <div class="armour-item-header">
                    <strong>EMPTY ARMOUR SLOT</strong>
                </div>
                <div class="armour-slot-row">
                    <div class="armour-slot-icon" aria-hidden="true">
                        <font-awesome-icon icon="shield-halved" />
                    </div>
                    <div class="armour-slot-content">
                        <div class="armour-slot-title">SLOT {{ occupiedSlots + n }}</div>
                        <div class="armour-special-title">SPECIAL</div>
                        <div class="armour-special-body">No armour equipped</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal to add armour -->
    <div
        class="modal fade"
        id="armourModal"
        tabindex="-1"
        aria-labelledby="armourModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title" id="armourModalLabel">
                        Add Armour
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    ></button>
                </div>

                <div class="modal-body">
                    <!-- Tabs -->
                    <ul class="nav nav-tabs mb-3" role="tablist">
                        <li class="nav-item">
                            <button
                                class="nav-link active"
                                data-bs-toggle="tab"
                                data-bs-target="#premadeArmourTab"
                                type="button"
                                role="tab"
                                @click="activeTab = 'premade'"
                            >
                                Premade
                            </button>
                        </li>
                        <li class="nav-item">
                            <button
                                class="nav-link"
                                data-bs-toggle="tab"
                                data-bs-target="#customArmourTab"
                                type="button"
                                role="tab"
                                @click="activeTab = 'custom'"
                            >
                                Custom
                            </button>
                        </li>
                    </ul>

                    <div class="tab-content">
                        <!-- Premade Armour -->
                        <div
                            class="tab-pane fade show active"
                            id="premadeArmourTab"
                            role="tabpanel"
                        >
                            <div class="input-group mb-3">
                                <span class="input-group-text"
                                    >Premade Armour</span
                                >
                                <select
                                    class="form-select"
                                    v-model="selectedArmour"
                                    @change="fillFromPreset"
                                >
                                    <option value="">Select...</option>
                                    <option
                                        v-for="item in rosterStore.armour.sort(
                                            (a, b) =>
                                                a.name.localeCompare(b.name)
                                        )"
                                        :key="item.name"
                                        :value="item.name"
                                    >
                                        {{ item.name }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="selectedArmour">
                                <h5>
                                    <strong>{{ newArmour.name }}</strong>
                                </h5>
                                <p>{{ newArmour.description }}</p>
                                <p>
                                    <strong>Armour Slots:</strong>
                                    {{ newArmour.armourSlots }}
                                </p>

                                <div v-if="newArmour.abilities.length">
                                    <strong>Abilities:</strong>
                                    <ul>
                                        <li
                                            v-for="(
                                                ab, i
                                            ) in newArmour.abilities"
                                            :key="i"
                                        >
                                            <strong>{{ ab.name }}</strong
                                            >: {{ ab.description }}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Custom Armour -->
                        <div
                            class="tab-pane fade"
                            id="customArmourTab"
                            role="tabpanel"
                        >
                            <div class="input-group mb-3">
                                <span class="input-group-text">Name</span>
                                <input
                                    v-model="newArmour.name"
                                    type="text"
                                    class="form-control"
                                />
                            </div>

                            <div class="input-group mb-3">
                                <span class="input-group-text"
                                    >Description</span
                                >
                                <textarea
                                    v-model="newArmour.description"
                                    class="form-control"
                                ></textarea>
                            </div>

                            <div class="input-group mb-3">
                                <span class="input-group-text">Armour Slots</span>
                                <select
                                    v-model="newArmour.armourSlots"
                                    class="form-select"
                                >
                                    <option disabled value="">Select...</option>
                                    <option v-for="n in MAX_ARMOUR_SLOTS" :key="n" :value="n">
                                        {{ n }}
                                    </option>
                                </select>
                            </div>

                            <!-- Abilities -->
                            <div class="input-group mb-3">
                                <span class="input-group-text">Abilities</span>
                                <select
                                    v-model="selectedAbility"
                                    class="form-select"
                                >
                                    <option disabled value="">Select...</option>
                                    <option
                                        v-for="ability in rosterStore.armourAbilities.sort(
                                            (a, b) =>
                                                a.name.localeCompare(b.name)
                                        )"
                                        :key="ability.name"
                                        :value="ability.name"
                                    >
                                        {{ ability.name }}
                                    </option>
                                </select>
                                <button
                                    class="btn btn-outline-primary"
                                    type="button"
                                    @click="addAbility"
                                >
                                    Add
                                </button>
                            </div>

                            <div class="input-group mb-3">
                                <span class="input-group-text"
                                    >Custom Ability</span
                                >
                                <input
                                    v-model="customAbilityName"
                                    type="text"
                                    class="form-control"
                                    placeholder="Name"
                                />
                                <input
                                    v-model="customAbilityDesc"
                                    type="text"
                                    class="form-control"
                                    placeholder="Description"
                                />
                                <button
                                    class="btn btn-outline-primary"
                                    type="button"
                                    @click="addCustomAbility"
                                >
                                    Add
                                </button>
                            </div>

                            <!-- Selected abilities -->
                            <div v-if="newArmour.abilities.length" class="mb-3">
                                <strong>Selected Abilities:</strong>
                                <div class="d-flex flex-wrap gap-2 mt-2">
                                    <span
                                        v-for="(ab, i) in newArmour.abilities"
                                        :key="i"
                                        class="badge bg-success d-flex align-items-center"
                                        style="
                                            font-size: 1rem;
                                            padding: 0.5em 0.75em;
                                        "
                                    >
                                        {{ ab.name }}
                                        <button
                                            type="button"
                                            class="btn-close btn-close-white ms-2"
                                            style="font-size: 0.6rem"
                                            @click="confirmRemoveAbility(i)"
                                        ></button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="multiply" class="me-1" />Close
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="addArmour(activeTab)"
                    >
                        <font-awesome-icon icon="plus" class="me-1" />Add Armour
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- View Armour Modal -->
    <div
        class="modal fade"
        id="viewArmourModal"
        tabindex="-1"
        aria-labelledby="viewArmourModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4
                        class="modal-title"
                        id="viewArmourModalLabel"
                        v-if="selectedArmour"
                    >
                        {{ selectedArmour }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    ></button>
                </div>
                <div class="modal-body" v-if="currentArmour">
                    <h5>
                        <strong>{{ currentArmour.name }}</strong>
                    </h5>
                    <p>{{ currentArmour.description }}</p>
                    <p>
                        <strong>Armour Slots:</strong>
                        {{ currentArmour.armourSlots }}
                    </p>

                    <p v-if="currentArmour.abilities?.length && !armourAbilitiesActive(currentArmour)" class="text-danger">Armour fully broken: abilities inactive.</p>
                    <div v-if="currentArmour.abilities?.length && armourAbilitiesActive(currentArmour)">
                        <strong>Abilities:</strong>
                        <ul>
                            <li
                                v-for="(ab, i) in currentArmour.abilities"
                                :key="i"
                            >
                                <strong>{{ ab.name }}</strong
                                >: {{ ab.description }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="multiply" class="me-1" />Close
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        @click="deleteArmour(currentArmour?.name)"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="trash-alt" class="me-1" />
                        Remove Armour
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useRosterStore } from "../stores/index";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    ARMOUR_SLOT_HP,
    MAX_ARMOUR_SLOTS,
    canEquipArmour,
    createSlotStates,
    functioningArmourSlots,
    armourAbilitiesActive,
    getArmourSlots,
    normalizeArmour,
    occupiedArmourSlots,
} from "../utils/armour";

export default {
    name: "ArmourDisplay",
    components: { FontAwesomeIcon },
    props: {
        character: { type: Object, required: true },
    },
    data() {
        return {
            ARMOUR_SLOT_HP,
            MAX_ARMOUR_SLOTS,
            selectedArmour: null,
            activeTab: "premade",
            newArmour: {
                name: "",
                armourSlots: "",
                description: "",
                abilities: [],
            },
            selectedAbility: "",
            customAbilityName: "",
            customAbilityDesc: "",
        };
    },
    created() {
        if (Array.isArray(this.character.armour)) {
            this.character.armour.forEach((item) => normalizeArmour(item));
        }
    },
    computed: {
        occupiedSlots() {
            return occupiedArmourSlots(this.character);
        },
        emptySlots() {
            return Math.max(0, MAX_ARMOUR_SLOTS - this.occupiedSlots);
        },
        ...mapStores(useRosterStore),
        currentArmour() {
            return Array.isArray(this.character.armour)
                ? this.character.armour.find(
                      (a) => a.name === this.selectedArmour
                  )
                : null;
        },
        abilityLookup() {
            const src = this.rosterStore?.armourAbilities ?? [];
            const map = new Map();
            if (Array.isArray(src)) {
                src.forEach((a) => {
                    if (a?.name)
                        map.set(a.name, {
                            name: a.name,
                            description: a.description ?? "",
                        });
                });
            } else if (src && typeof src === "object") {
                Object.entries(src).forEach(([key, val]) => {
                    if (val && typeof val === "object") {
                        const name = val.name ?? key;
                        map.set(name, {
                            name,
                            description: val.description ?? "",
                        });
                    } else if (typeof val === "string") {
                        map.set(key, { name: key, description: val });
                    }
                });
            }
            return map;
        },
    },
    methods: {
        armourAbilitiesActive(item) {
            return armourAbilitiesActive(item);
        },
        normalizeItem(item) {
            return normalizeArmour(item);
        },
        armourSlots(item) {
            return getArmourSlots(item);
        },
        slotStates(item) {
            const states = Array.isArray(item?.slotStates) ? item.slotStates : [];
            const slots = getArmourSlots(item);
            const baseStates = createSlotStates(slots, states);
            if (!states.length && Number(item?.damage) > 0) {
                const damaged = Math.min(slots, Math.floor(Number(item.damage)));
                return baseStates.map((slot, index) =>
                    index < damaged ? { ...slot, hp: 0 } : slot
                );
            }
            return baseStates;
        },
        armourDice(item) {
            return this.slotStates(item).filter((slot) => slot.hp > 0).length;
        },
        armourAbilities(item) {
            const source = Array.isArray(item?.abilities)
                ? item.abilities
                : item?.ability
                ? [item.ability]
                : [];
            return source.map((ability) => {
                if (typeof ability === "string") {
                    const stored = this.abilityLookup.get(ability);
                    return { name: ability, description: stored?.description || "" };
                }
                return { name: ability?.name || "", description: ability?.description || "" };
            }).filter((ability) => ability.name || ability.description);
        },
        armourSpecialText(item) {
            return this.armourAbilities(item)
                .map((ability) => [ability.name, ability.description].filter(Boolean).join(": "))
                .join(" ");
        },
        rollArmourDice(item) {
            const dice = this.armourDice(item);
            if (dice > 0) {
                this.$emit("roll-dice", { skill: dice, talent: 0, statKey: "grit" });
            }
        },
        toggleSlotDamage(item, slotIndex, hpPoint) {
            const normalized = normalizeArmour(item);
            const slot = normalized.slotStates[slotIndex];
            if (!slot) return;
            slot.hp = slot.hp === hpPoint ? hpPoint - 1 : hpPoint;
        },
        resetNewArmour() {
            this.newArmour = {
                name: "",
                armourSlots: "",
                description: "",
                abilities: [],
            };
            this.selectedAbility = "";
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        fillFromPreset() {
            if (!this.selectedArmour) {
                this.resetNewArmour();
                return;
            }
            const preset = this.rosterStore.armour.find(
                (a) => a.name === this.selectedArmour
            );
            if (preset) {
                this.newArmour = normalizeArmour({
                    ...preset,
                    abilities: Array.isArray(preset.abilities)
                        ? preset.abilities.map((a) => {
                              if (typeof a === "string") {
                                  const storeAb = this.abilityLookup.get(a);
                                  return {
                                      name: a,
                                      description:
                                          storeAb?.description ||
                                          "No description available.",
                                  };
                              }
                              return { ...a };
                          })
                        : [],
                });
            }
        },
        ensureUniqueAbilityName(name) {
            let newName = name;
            let counter = 65;
            while (
                this.rosterStore.armourAbilities.some((a) => a.name === newName)
            ) {
                newName = `${name} (${String.fromCharCode(counter)})`;
                counter++;
            }
            return newName;
        },
        addArmour(source = "custom") {
            if (!this.newArmour.name) {
                alert("Armour must have a name!");
                return;
            }

            if (
                !this.newArmour.armourSlots ||
                isNaN(this.newArmour.armourSlots) ||
                this.newArmour.armourSlots < 1 ||
                this.newArmour.armourSlots > MAX_ARMOUR_SLOTS
            ) {
                alert("Armour Slots must be between 1 and 3.");
                return;
            }

            // check for unadded custom ability
            if (
                this.customAbilityName.trim() ||
                this.customAbilityDesc.trim()
            ) {
                alert(
                    "You have entered a custom ability but not added it. Please click 'Add' to include it."
                );
                return;
            }

            if (this.selectedAbility !== "") {
                alert(
                    "You have selected an ability but not added it. Please click 'Add' to include it."
                );
                return;
            }

            const armourToAdd = normalizeArmour({
                ...this.newArmour,
                abilities: this.newArmour.abilities.map((a) => ({ ...a })),
            });
            if (!canEquipArmour(this.character, armourToAdd)) {
                alert(`Armour exceeds the ${MAX_ARMOUR_SLOTS}-slot capacity.`);
                return;
            }
            if (!Array.isArray(this.character.armour)) {
                this.$set(this.character, "armour", []); // ensure array
            }
            this.character.armour.push(armourToAdd);

            if (source === "custom") {
                if (
                    !this.rosterStore.armour.find(
                        (a) => a.name === armourToAdd.name
                    )
                ) {
                    this.rosterStore.armour.push(armourToAdd);
                }
                for (const ability of armourToAdd.abilities) {
                    if (ability.custom) {
                        const uniqueName = this.ensureUniqueAbilityName(
                            ability.name
                        );
                        const abilityToAdd = { ...ability, name: uniqueName };
                        delete abilityToAdd.custom;
                        this.rosterStore.armourAbilities.push(abilityToAdd);
                    }
                }
            }

            this.selectedArmour = null;
            this.resetNewArmour();
            alert("Armour added!");
        },
        deleteArmour(name) {
            if (!name) return;
            if (!Array.isArray(this.character.armour)) return;
            if (confirm(`Are you sure you want to delete ${name}?`)) {
                const index = this.character.armour.findIndex(
                    (a) => a.name === name
                );
                if (index !== -1) this.character.armour.splice(index, 1);
                this.selectedArmour = null;
            }
        },
        addAbility() {
            if (!this.selectedAbility) return;
            if (
                !this.newArmour.abilities.find(
                    (a) => a.name === this.selectedAbility
                )
            ) {
                const storeAb = this.abilityLookup.get(this.selectedAbility);
                this.newArmour.abilities.push({
                    name: this.selectedAbility,
                    description:
                        storeAb?.description || "No description available.",
                    custom: false,
                });
            }
            this.selectedAbility = "";
        },
        addCustomAbility() {
            const name = this.customAbilityName.trim();
            const desc = this.customAbilityDesc.trim();

            if (!name || !desc) {
                alert(
                    "Custom abilities must have both a Name and a Description!"
                );
                return;
            }
            if (!this.customAbilityName.trim()) return;
            this.newArmour.abilities.push({
                name: this.customAbilityName.trim(),
                description: this.customAbilityDesc.trim() || "No description.",
                custom: true,
            });
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        confirmRemoveAbility(index) {
            const ab = this.newArmour.abilities[index];
            if (confirm(`Remove ability "${ab.name}"?`)) {
                this.newArmour.abilities.splice(index, 1);
            }
        },
    },
};
</script>

<style scoped>
.armour-display {
    padding: 10px 5px;
    border: solid 2px #555;
    border-radius: 8px;
}
.armour-board {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}
.armour-item-card {
    overflow: hidden;
    border: 2px solid currentColor;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.28);
}
.armour-item-header {
    min-height: 2rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.35rem 0.6rem;
    background: #050505;
    color: #fff;
}
.armour-item-name {
    cursor: pointer;
    flex: 1;
}
.armour-item-dice {
    white-space: nowrap;
    font-size: 0.8rem;
}
.armour-roll-button {
    width: 2rem;
    height: 2rem;
    padding: 0;
}
.armour-slot-list {
    display: flex;
    flex-direction: column;
}
.armour-slot-row {
    display: grid;
    grid-template-columns: 5.4rem minmax(0, 1fr);
    min-height: 7rem;
    border-top: 1px solid currentColor;
}
.armour-slot-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 2px solid currentColor;
    font-size: 3.5rem;
}
.armour-slot-content {
    min-width: 0;
    display: flex;
    flex-direction: column;
}
.armour-slot-title,
.armour-special-title {
    padding: 0.15rem 0.45rem;
    background: #050505;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    text-align: center;
}
.armour-slot-health,
.armour-special-body {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 2rem;
    padding: 0.25rem 0.45rem;
}
.armour-special-body {
    flex: 1;
    background: rgba(220, 230, 245, 0.82);
    color: #111;
    font-size: 1rem;
    font-weight: 400;
}
.armour-hp-track {
    display: flex;
    flex: 1 1 auto;
    width: 100%;
    gap: 0.25rem;
}
.armour-hp-point {
    flex: 0 0 calc(50% - 0.125rem);
    width: calc(50% - 0.125rem);
    min-width: 0;
    height: 1.35rem;
    border: 1px solid #275a92;
    border-radius: 2px;
    background: rgba(39, 90, 146, 0.18);
    cursor: pointer;
}
.armour-hp-point.filled {
    background: #397fc4;
}
.armour-special-struck {
    text-decoration: line-through;
    text-decoration-thickness: 2px;
}
.armour-special-inactive {
    margin-left: auto;
    color: #8b1e1e;
    font-weight: 700;
}
.armour-empty-card {
    opacity: 0.72;
}
</style>